import React from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  paleChipSx,
  severityFromInterpretation
} from 'src/components/UI/chips/paleChipSx';
import { TILE_COLOURS } from '../../tileColours';
import { formatDate } from 'src/util/util';

const HGVS_FIELDS = [
  { key: 'variantG', prefix: 'g.' },
  { key: 'variantC', prefix: 'c.' },
  { key: 'variantP', prefix: 'p.' }
];

const renderHgvs = (variant) => {
  const parts = HGVS_FIELDS.map(({ key, prefix }) => {
    const raw = variant[key];
    if (!raw) return null;
    return raw.startsWith(prefix) ? raw : `${prefix}${raw}`;
  }).filter(Boolean);

  if (parts.length === 0) return variant.gene || 'Unspecified variant';
  return parts.join(' · ');
};

const SavedVariantTile = ({
  variant,
  onDelete,
  onEdit,
  isEditing,
  disabled
}) => {
  const labSeverity = severityFromInterpretation(variant.variantInterpretation);

  return (
    <Box
      sx={{
        border: `1px solid ${TILE_COLOURS.genotype.border}`,
        borderLeft: `3px solid ${TILE_COLOURS.genotype.accent}`,
        backgroundColor: TILE_COLOURS.genotype.background,
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        mb: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        opacity: isEditing ? 0.55 : 1,
        outline: isEditing
          ? `2px dashed ${TILE_COLOURS.genotype.accent}`
          : 'none'
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
            flexWrap: 'wrap'
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: TILE_COLOURS.genotype.text,
              lineHeight: 1
            }}
          >
            Variant
          </Typography>
          {variant.gene && (
            <Typography
              variant="caption"
              sx={{ color: TILE_COLOURS.genotype.subtext }}
            >
              {variant.gene}
            </Typography>
          )}
        </Box>

        <Typography
          sx={{
            mt: 0.5,
            fontFamily: 'monospace',
            fontSize: 13,
            wordBreak: 'break-word'
          }}
        >
          {renderHgvs(variant)}
        </Typography>

        {variant.dateLastReviewed && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
          >
            Reviewed: {formatDate(variant.dateLastReviewed)}
          </Typography>
        )}

        {variant.variantInterpretation && (
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={`Lab: ${variant.variantInterpretation}`}
              sx={paleChipSx(labSeverity)}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {onEdit && (
          <Tooltip title={isEditing ? 'Editing variant' : 'Edit variant'}>
            <span>
              <IconButton
                size="small"
                onClick={onEdit}
                disabled={disabled || isEditing}
                aria-label="Edit variant"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Remove variant">
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={disabled}
                aria-label="Remove variant"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default SavedVariantTile;
